import { saveAs } from "file-saver";
import JSZip from "jszip";
import React, { useContext, useState } from "react";
import GratefulDB, { Track } from "../../GratefulDB";
import GratefulDBContext from "../../GratefulDBContext";
import "./DownloadButton.scss";
import { constructTrackUrl } from "./Helpers";
import { usePassword } from "./usePassword";

const DownloadButton: React.FC<{
  showId: string;
}> = (props): JSX.Element => {
  const [isDownloading, setIsDownloading] = useState(false);
  const gratefulDB = useContext<GratefulDB>(GratefulDBContext);
  const password = usePassword();

  const entry = gratefulDB.get(props.showId);

  const sanitizeFileName = (name: string): string => {
    return name.replace(/[^a-z0-9]/gi, "_").replace(/_+/g, "_");
  };

  const fetchTrackBatch = async (
    trackBatch: Track[]
  ): Promise<{ track: Track; blob: Blob }[]> => {
    const promises = trackBatch.map(async (track) => {
      if (!entry) {
        throw new Error("Entry not found");
      }
      const url = constructTrackUrl(entry, track, password.getHash());
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download ${track.title}`);
      }
      const blob = await response.blob();
      return { track, blob };
    });

    return Promise.all(promises);
  };

  const handleDownload = async () => {
    if (!entry || isDownloading) {
      return;
    }

    setIsDownloading(true);

    try {
      const zip = new JSZip();
      const tracks = entry.tracks;
      const batchSize = 5;

      for (let i = 0; i < tracks.length; i += batchSize) {
        const batch = tracks.slice(i, i + batchSize);
        const results = await fetchTrackBatch(batch);
        results.forEach(({ track, blob }) => {
          const paddedTrackNumber = track.track.padStart(2, "0");
          const sanitizedTitle = sanitizeFileName(track.title);
          const fileName = `${paddedTrackNumber}-${sanitizedTitle}.mp3`;
          zip.file(fileName, blob);
        });
      }

      const zipContent = await zip.generateAsync({ type: "blob" });
      const sanitizedVenue = sanitizeFileName(entry.venue);
      const zipFileName = `${entry.date}-${sanitizedVenue}.zip`;

      saveAs(zipContent, zipFileName);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!entry) {
    return <></>;
  }

  return (
    <div
      className={`DownloadButton ui button compact tiny basic mobile-hidden ${
        isDownloading ? "teal" : "secondary"
      }`}
      tabIndex={0}
      onClick={handleDownload}
    >
      {isDownloading ? (
        <>
          <i className="spinner loading icon" />
          Download
        </>
      ) : (
        <>
          <i className="download icon" />
          Download
        </>
      )}
    </div>
  );
};

export default DownloadButton;
